import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";
import { useElements, useStripe } from "@stripe/react-stripe-js";

import Collapsible from "@kikoff/components/src/v1/containers/Collapsible";
import Switch from "@kikoff/components/src/v1/inputs/Switch";
import useError from "@kikoff/hooks/src/useError";
import { ErrorProvider } from "@kikoff/hooks/src/useErrorConsumer";
import useId from "@kikoff/hooks/src/useId";
import useObjectState from "@kikoff/hooks/src/useObjectState";
import { format } from "@kikoff/utils/src/string";

import { UnderlineButton } from "@component/buttons/buttons";
import Detail from "@component/dashboard/account/detail";
import PageAlign from "@component/dashboard/offer/page_align";
import SectionCard from "@component/dashboard/offer/section_card";
import ViewTemplate from "@component/dashboard/offer/view_template";
import WithFooter from "@component/dashboard/offer/with_footer";
import PaymentPlan, {
  PaymentSchedule,
} from "@component/dashboard/store/payment_plan";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import AgreementText, { DocLink } from "@component/legal/agreement_text";
import Line from "@component/line";
import PaymentMethod from "@component/payment/base";
import { setupCreditLineRepayment } from "@feature/credit_line";
import { selectHasPaymentMethod } from "@feature/funds";
import {
  dismiss,
  provideConsumable,
  selectDismissable,
  selectFeatureFlag,
} from "@feature/page";
import { getCreditAccountMentalModelName } from "@src/constants";
import { track } from "@util/analytics";
import { nativeDispatch } from "@util/mobile";
import { accountAutoPayEnabled } from "@util/payments";

import styles from "./setup_repayment.module.scss";

interface SetupRepaymentViewProps extends PageRegistration {
  onPaymentSelection?(token: string): void;
  activating?: boolean;
}

const SetupRepaymentView: React.FC<SetupRepaymentViewProps> = ({
  configFlow,
  Button,
  page,
  onPaymentSelection,
  activating,
}) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const paymentMethodError = useError();

  const creditLine = useSelector((state) => state.creditLine.account);
  const hasPaymentMethod = useSelector(selectHasPaymentMethod());
  const caPaymentTutorialEnabled = useSelector(
    selectFeatureFlag("ca_payment_tutorial")
  );
  const caAggressivePayment = useSelector(
    selectFeatureFlag("ca_aggressive_payment")
  );
  const isPayable = creditLine.outstandingMinimumPaymentCents > 0;
  const shouldPayNow =
    isPayable && (!caPaymentTutorialEnabled || caAggressivePayment);
  const preview = useSelector((state) => state.shopping.checkoutPreview);

  const [autopay, setAutopay] = useObjectState({
    enabled: true,
    agreed: false,
  });
  const [paymentMethodToken, setPaymentMethodToken] = useState<string>();
  const [validCardEntry, setValidCardEntry] = useState(false);
  const [pendingText, setPendingText] = useState("Please wait...");

  const isSubmittable = paymentMethodToken || validCardEntry;

  useEffect(() => {
    if (activating && (caPaymentTutorialEnabled || caAggressivePayment))
      dispatch(provideConsumable("caPaymentTutorial"));
  }, []);

  configFlow.button(
    <Button
      disabled={!isSubmittable}
      onClick={async (e) => {
        if (autopay.enabled) dispatch(dismiss("AUTOPAY_TERMS"));
        await dispatch(
          setupCreditLineRepayment({
            stripe,
            elements,
            autopayEnabled: autopay.enabled,
            onNewPaymentMethodToken: onPaymentSelection,
            onUpdate(text) {
              setPendingText(text);
            },
            payCreditLine: shouldPayNow,
            paymentMethodToken,
          })
        ).catch((err) => {
          if (err?.name === "PaymentMethodError") {
            // Direct user to card issuer if card is immediately declined.
            const message =
              err.code === "card_declined"
                ? err.message +
                  " Please contact your card issuer or try a different card."
                : err.message;
            paymentMethodError.throw(message);
            // Prevent global error, error will be shown in payment method card
            e.preventError();
          }
          throw err;
        });
      }}
      text={
        shouldPayNow
          ? {
              pending: pendingText,
              success: "Payment Success",
            }
          : {
              pending: pendingText,
              success: "Confirmed",
            }
      }
    >
      {shouldPayNow ? "Submit payment" : "Confirm"}
    </Button>,
    [
      paymentMethodToken,
      autopay,
      creditLine,
      pendingText,
      isSubmittable,
      shouldPayNow,
    ]
  );

  const paymentSchedule = (preview || creditLine)?.paymentSchedule;

  return (
    <WithFooter
      footer={
        <PageAlign wrap>
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            Not ready?{" "}
            <UnderlineButton
              onClick={() => {
                track.tap(
                  "CA - Payment - Setup Repayment - Return To Dashboard",
                  {
                    "screen name": "payment",
                  }
                );
                if (!nativeDispatch("close")) Router.push("/dashboard");
              }}
            >
              Return to dashboard
            </UnderlineButton>
          </div>
        </PageAlign>
      }
    >
      <PageAlign wrap>
        <div className={styles["payment-view"]}>
          <header data-cy="setup-repayment-header">
            {isPayable
              ? "Start building positive payment history"
              : creditLine
              ? "Review repayment"
              : "Set up repayment"}
          </header>
          <ViewTemplate
            note={
              hasPaymentMethod
                ? "Here is your updated repayment schedule."
                : "Link your bank account by providing your card information."
            }
          >
            {paymentSchedule && !isPayable && (
              <PaymentPlan paymentSchedule={paymentSchedule} first />
            )}
            <ErrorProvider error={paymentMethodError}>
              <SectionCard
                header="Payment method"
                className={styles["payment-method"]}
              >
                <PaymentMethod
                  onPaymentMethodTokenChange={setPaymentMethodToken}
                  onValidChange={setValidCardEntry}
                />
              </SectionCard>
            </ErrorProvider>
            <AutopaySection autopayState={autopay} onChange={setAutopay} />
            {isPayable && (
              <SectionCard header="Repayment Overview">
                <Detail label="Payment due">
                  {format.money(creditLine.outstandingMinimumPaymentCents)}
                </Detail>
                <Detail label="Next payment due date">
                  {format.date(creditLine.paymentDueDate, "mm/dd/YYYY")}
                </Detail>
                <Line spacing={16} />
                <PaymentSchedule paymentSchedule={paymentSchedule} />
              </SectionCard>
            )}
          </ViewTemplate>
          {!isPayable && (
            <div className={styles.notice}>
              We’ll email you before your next payment is due.
            </div>
          )}
        </div>
      </PageAlign>
    </WithFooter>
  );
};
export default SetupRepaymentView;

interface AutopayProps {
  autopayState: { enabled: boolean; agreed: boolean };
  onChange(update: Partial<AutopayProps["autopayState"]>): void;
}

export const AutopaySection: React.FC<AutopayProps> = ({
  autopayState,
  onChange,
}) => {
  const creditLine = useSelector((state) => state.creditLine.account);
  const showTerms = useSelector(selectDismissable("AUTOPAY_TERMS"));

  const isPayable = creditLine.outstandingMinimumPaymentCents > 0;

  const id = useId();

  return (
    <SectionCard header="Autopay" className={styles.autopay}>
      <label className={styles["switch-label"]} htmlFor={id}>
        <header>Automate payments</header>
        <div className={styles["switch-container"]}>
          <div className={styles.value}>
            {autopayState.enabled ? "On" : "Off"}
          </div>
          <Switch
            id={id}
            checked={autopayState.enabled}
            onChange={(e) => {
              onChange({ enabled: e.currentTarget.checked });
            }}
          />
        </div>
      </label>
      {!accountAutoPayEnabled(creditLine) && (
        <Collapsible collapse={!autopayState.enabled}>
          {isPayable ? (
            <p>
              Starting next month, we will automatically process your payment
              1-week after your statement closing date each month.
            </p>
          ) : (
            <p>
              We will automatically process your{" "}
              {getCreditAccountMentalModelName()} minimum payment 1-week after
              your statement closes each month.
            </p>
          )}
          <AgreementText size="small" by="continuing">
            {showTerms && autopayState.enabled && (
              <DocLink href="/autopay-agreement.pdf" type="pdf">
                AutoPay terms
              </DocLink>
            )}
          </AgreementText>
        </Collapsible>
      )}
    </SectionCard>
  );
};
